import React from 'react';
import './Button.css';

/**
 * Reusable Button component
 * @param {string} variant - 'primary' or 'secondary'
 * @param {string} size - 'small', 'medium', or 'large'
 * @param {boolean} disabled - Whether button is disabled
 * @param {string} ariaLabel - Accessibility label
 * @param {function} onClick - Click handler
 * @param {string} href - Optional URL or anchor link (renders as <a> instead of <button>)
 * @param {boolean} showIcon - Whether to render the trailing arrow icon on primary buttons (default: true)
 * @param {React.ReactNode} leadingIcon - Optional icon rendered before the label (decorative, aria-hidden)
 * @param {React.ReactNode} children - Button content
 */
const Button = ({
  variant = 'primary',
  size = 'large',
  disabled = false,
  ariaLabel,
  onClick,
  href,
  showIcon = true,
  leadingIcon,
  children,
  className = '',
  ...props
}) => {
  const buttonClasses = [
    'button',
    `button--${variant}`,
    `button--${size}`,
    disabled ? 'button--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  const handleClick = (e) => {
    if (!disabled && onClick) {
      onClick(e);
    }
  };

  const content = (
    <>
      {leadingIcon && (
        <span className="button-icon button-icon--leading" aria-hidden="true">
          {leadingIcon}
        </span>
      )}
      <span className="button-text">{children}</span>
      {variant === 'primary' && showIcon && (
        <span className="button-icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="0" y1="7" x2="14" y2="7" stroke="currentColor" strokeWidth="2"/>
            <line x1="7" y1="0" x2="14" y2="7" stroke="currentColor" strokeWidth="2"/>
            <line x1="7" y1="14" x2="14" y2="7" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </span>
      )}
    </>
  );

  // Render as anchor if href is provided
  if (href) {
    return (
      <a
        className={buttonClasses}
        href={href}
        aria-label={ariaLabel}
        onClick={handleClick}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      className={buttonClasses}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      type="button"
      onClick={handleClick}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button;
